import React, { createContext, useContext, useEffect, useReducer } from 'react'
import { fetcher, staticOptions } from 'src/lib/core/api-client'
import useSWR from 'swr'
import { apiURI } from 'src/lib/utils/constants'
import { useAuth } from './AuthContext'
import { checkShopConnectionStatus, parseOrganisationData } from '../utils/OrganisationHelpers'
import { getShopfloAppsParsedData } from '../utils/AppStoreHelper'
import { AppStoreAppType } from '../types/AppStoreTypes'
import { PocDetails } from '../types/billing'
import { OnboardingStep } from '../types/onboardingV2'
import { OnboardingStatusType } from '../types/onboarding'

enum OrganisationActionType {
  SET_ORGANISATION_DATA = 'SET_ORGANISATION_DATA',
  SET_OVER_DUE_INVOICES_BANNER_OPEN = 'SET_OVER_DUE_INVOICES_BANNER_OPEN',
  SET_PAY_NOW_CLICKED = 'SET_PAY_NOW_CLICKED',
  SET_SHOPFLO_APPS = 'SET_SHOPFLO_APPS',
  SET_SUPER_ADMIN_ENABLED = 'SET_SUPER_ADMIN_ENABLED'
}

type OrganisationAction =
  | { type: OrganisationActionType.SET_ORGANISATION_DATA; payload: any }
  | { type: OrganisationActionType.SET_OVER_DUE_INVOICES_BANNER_OPEN; payload: boolean }
  | { type: OrganisationActionType.SET_PAY_NOW_CLICKED; payload: boolean }
  | { type: OrganisationActionType.SET_SHOPFLO_APPS; payload: any }
  | { type: OrganisationActionType.SET_SUPER_ADMIN_ENABLED; payload: boolean }

export type OverDueStatus = 'OVERDUE' | 'INVOICES_GENERATED' | 'REMINDER_STATE' | 'NONE'

enum BusinessType {
  REGISTERED_BUSINESS_REGULAR = 'REGISTERED_BUSINESS_REGULAR',
  UNREGISTERED_BUSINESS = 'UNREGISTERED_BUSINESS'
}

export interface OrganisationData {
  id: string
  name: string
  metadata: {
    shopfloPoc: number
    bannerText: string
  }
  taxDetails: {
    category: string
    pocDetails: {
      pocName: string
      pocEmail: string
      pocPhone: string
      isPrimaryPoc: boolean
      designation: string
    }[]
    billingAddress?: {
      address1: string
      address2: string
      city: string
      state: string
      country: string
      zip: string
      state_code: string
    } | null
    shippingAddress?: {
      address1: string
      address2: string
      city: string
      state: string
      country: string
      zip: string
      state_code: string
    } | null
    entityType?: BusinessType
    gstCertificateFileKey?: string
    identificationNumber?: string
    businessLegalName?: string
    businessTradeName?: string
    areAddressesSame?: boolean
  }
  billingPopUp: {
    visible: boolean
    dismissible: boolean
    status: OverDueStatus
    sentInvoiceMonth: string
    sentInvoiceDueDate: string
    numberOfOverDueInvoices: number
    overDueAmount: string
    overDueSince: number
    hasOverdueInvoices: boolean
  }
  isOverDueInvoicesBannerOpen: boolean
  createdAt: string
  payNowClicked: boolean
  shopfloApps?: AppStoreAppType[]
  superAdminEnabled: boolean
  inOnboarding: boolean
  version?: string
  isFromShopifyPublicApp: boolean
  shopConnectionStatus?: OnboardingStatusType
  onboardingState: {
    completed_states: OnboardingStep[]
    current_state: OnboardingStep
    unlockedStates: OnboardingStep[]
  }
  shopDomain: string
  status: string
  themeDetails: Array<{
    name: string
    theme_id: string
  }>
  churnReason: string
  churnReasonComments: string
  churnReviewMeetingId: string
  organizationAgreement: {
    status: 'PENDING' | 'ACCEPTED' | 'UNAPPLICABLE'
  } | null
}

const initialOrganisationData: OrganisationData = {
  id: '',
  name: '',
  metadata: {
    shopfloPoc: 0,
    bannerText: ''
  },
  taxDetails: {
    category: '',
    pocDetails: []
  },
  billingPopUp: {
    visible: false,
    dismissible: false,
    status: 'NONE',
    sentInvoiceMonth: '',
    sentInvoiceDueDate: '',
    numberOfOverDueInvoices: 0,
    overDueAmount: '0',
    overDueSince: 0,
    hasOverdueInvoices: false
  },
  isOverDueInvoicesBannerOpen: true,
  createdAt: '',
  payNowClicked: false,
  superAdminEnabled: false,
  shopfloApps: [],
  inOnboarding: false,
  version: 'stable',
  isFromShopifyPublicApp: false,
  onboardingState: {
    completed_states: [],
    current_state: OnboardingStep.user_profile,
    unlockedStates: []
  },
  shopDomain: '',
  status: '',
  themeDetails: [],
  churnReason: "",
  churnReasonComments: "",
  churnReviewMeetingId: "",
  organizationAgreement: null
}

interface OrganisationTypeContext {
  state: OrganisationData
  actions: {
    setOrganisationData: (data: any) => void
    setOverDueInvoicesBannerOpen: (value: boolean) => void
    setPayNowClicked: (value: boolean) => void
    setShopfloApps: (data: any) => void
    setSuperAdminEnabled: (value: boolean) => void
    refetchOrganisation: () => void
    parseAndSetOrganisationData: (orgResponse: any) => void
  }
  getters: {
    organisationData: OrganisationData
    billingPopUp: OrganisationData['billingPopUp']
    status: OverDueStatus
    hasOverdueInvoices: boolean
    isOverDueInvoicesBannerOpen: boolean
    pocDetails: PocDetails[]
    createdAt: string
    payNowClicked: boolean
    shopfloApps?: AppStoreAppType[]
    version: string
    isFromShopifyPublicApp: boolean
    bannerText: string
    isOverDueBanner: boolean
    isShopConnected: boolean
    /** The /organization request has settled (data or error) — `organisationData` is final for this load. */
    isOrganisationResolved: boolean
    isOrgDetailsCompleted: boolean
  }
}

function organisationReducer(state: OrganisationData, action: OrganisationAction): OrganisationData {
  switch (action.type) {
    case OrganisationActionType.SET_ORGANISATION_DATA:
      return { ...state, ...action.payload }
    case OrganisationActionType.SET_OVER_DUE_INVOICES_BANNER_OPEN:
      return { ...state, isOverDueInvoicesBannerOpen: action.payload }
    case OrganisationActionType.SET_PAY_NOW_CLICKED:
      return { ...state, payNowClicked: action.payload }
    case OrganisationActionType.SET_SHOPFLO_APPS:
      return { ...state, shopfloApps: action.payload }
    case OrganisationActionType.SET_SUPER_ADMIN_ENABLED:
      return { ...state, superAdminEnabled: action.payload }
    default:
      return state
  }
}

const OrganisationContext = createContext<OrganisationTypeContext | undefined>(undefined)

const OrganisationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { loggedIn } = useAuth()
  const {
    data: orgResponse,
    error: orgError,
    isValidating,
    mutate: mutateOrganisation
  } = useSWR(
    loggedIn ? [`/organization`, apiURI.AUTH_ENGINE] : null,
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )

  const { data: shopfloAppsData, isLoading } = useSWR(
    loggedIn ? [`/shopflo-apps?only-visible=false`, apiURI.SETTLEMENT_ENGINE] : null,
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )

  const [state, dispatch] = useReducer(organisationReducer, initialOrganisationData)

  useEffect(() => {
    if (isValidating || !orgResponse) return
    const { data } = orgResponse as any
    if (!data) return
    const parsedData = parseOrganisationData(data)
    setOrganisationData(parsedData)
  }, [isValidating, orgResponse])

  useEffect(() => {
    if (isLoading || !shopfloAppsData) return

    const { data } = shopfloAppsData as any
    const parsedShopfloApps = getShopfloAppsParsedData(data)
    setShopfloApps(parsedShopfloApps)
  }, [shopfloAppsData, isLoading])

  const setShopfloApps = (data: any) => {
    dispatch({ type: OrganisationActionType.SET_SHOPFLO_APPS, payload: data })
  }

  const setOrganisationData = (data: any) => {
    dispatch({ type: OrganisationActionType.SET_ORGANISATION_DATA, payload: data })
  }

  const setOverDueInvoicesBannerOpen = (value: boolean) => {
    dispatch({ type: OrganisationActionType.SET_OVER_DUE_INVOICES_BANNER_OPEN, payload: value })
  }

  const setPayNowClicked = (value: boolean) => {
    dispatch({ type: OrganisationActionType.SET_PAY_NOW_CLICKED, payload: value })
  }

  const setSuperAdminEnabled = (value: boolean) => {
    dispatch({ type: OrganisationActionType.SET_SUPER_ADMIN_ENABLED, payload: value })
  }

  const parseAndSetOrganisationData = (orgResponse: any) => {
    const { data } = orgResponse as any
    if (!data) return
    const parsedData = parseOrganisationData(data)
    setOrganisationData(parsedData)
  }

  const refetchOrganisation = () => {
    mutateOrganisation()
  }

  const actions = {
    setOrganisationData,
    setOverDueInvoicesBannerOpen,
    setPayNowClicked,
    setShopfloApps,
    setSuperAdminEnabled,
    refetchOrganisation,
    parseAndSetOrganisationData
  }

  const getters = {
    organisationData: state,
    billingPopUp: state.billingPopUp,
    status: state.billingPopUp.status,
    hasOverdueInvoices: state.billingPopUp.hasOverdueInvoices,
    isOverDueInvoicesBannerOpen: state.isOverDueInvoicesBannerOpen,
    pocDetails: state.taxDetails.pocDetails,
    createdAt: state.createdAt,
    payNowClicked: state.payNowClicked,
    shopfloApps: state.shopfloApps,
    version: state.version ?? 'stable',
    isFromShopifyPublicApp: state.isFromShopifyPublicApp,
    bannerText: state.metadata.bannerText,
    isOverDueBanner: state.billingPopUp.status === 'OVERDUE',
    isShopConnected: checkShopConnectionStatus(state.status, state.onboardingState),
    // Parsed data reaches `state` one effect after the response, so key off the parsed id, not the raw response.
    // Logged out, the fetch never runs, so there is nothing left to wait for.
    isOrganisationResolved:
      !loggedIn ||
      Boolean(state.id) ||
      orgError !== undefined ||
      (orgResponse !== undefined && !(orgResponse as { data?: unknown }).data),
    isOrgDetailsCompleted: state.onboardingState.completed_states.includes(OnboardingStep.org_details)
  }

  return <OrganisationContext.Provider value={{ state, actions, getters }}>{children}</OrganisationContext.Provider>
}

export const useOrganisationContext = () => {
  const context = useContext(OrganisationContext)
  if (!context) {
    throw new Error('useOrganisation must be used within an OrganisationProvider')
  }
  return context
}

export default OrganisationProvider
